import React from 'react';
import Icon from '../../shared/Icon';
import './AlertSteps.css';
import ReportStepQuery from './ReportStepQuery';
import AlertThresholdFields from './AlertThresholdFields';
import AlertMatchCount from './AlertMatchCount';
import AlertSpecProblems from './AlertSpecProblems';
import AlertQueryPreview from './AlertQueryPreview';
import { useAlertIrAuthor } from '../useAlertIrAuthor';

/**
 * Step 2 — Query and threshold.
 *
 * "Keep the Report query step exactly" is taken literally: the Report query
 * step is *reused*, not cloned. The alert name, dataset multi-select with its
 * inline "a dataset is required" validation, the saved-query dropdown and the
 * editable query are therefore the same component and cannot drift from the
 * Reports flow.
 *
 * Two controls inside it are replaced rather than reused, through slots that
 * step exposes — and both for one reason. REGENERATE runs the chat query and
 * the panel below it shows that query's narrative answer; on an alert, neither
 * is the query the alert runs. Leaving them in place gave the author a button
 * that previews one definition of their alert and a comparison built from
 * another, which is exactly the split this epic closes. So the button builds
 * the alert's own query, and the panel shows that query's rows. Raised in
 * review of #177, against a live stack.
 *
 * Everything else alert-specific is added below, which is where the ticket puts
 * it and where the live match count belongs — the count is a consequence of the
 * threshold, so it reads after the fields that determine it.
 *
 * The dry run is owned by the wizard and passed in, so one evaluation serves
 * every step rather than one per mount.
 */
const AlertStepQuery = ({ formData, onSetFormData, dryRun, problems = [], notReadyReason }) => {
  const alert = formData.alert ?? {};
  const author = useAlertIrAuthor(onSetFormData, alert.authorChoices);
  const question = (formData.reportQuery ?? formData.queryName ?? '').trim();
  const datasetIds = formData.reportDatasetIds ?? [];
  // Both are required by the endpoint — `datasetIds` has `min_length=1` — so a
  // call without them is a 422 rendered as an error banner while the author is
  // still filling the form in.
  const canAuthor = question.length > 0 && datasetIds.length > 0 && !author.loading;

  /*
    The question becomes the query here, explicitly.

    Not on a timer and not on every keystroke: each press is a real query
    against the structured-data service, and this is also the moment the
    author's sentence stops being editable prose and becomes the thing the
    alert runs. The comparison below is pre-filled from the result as a
    suggestion they then confirm or change.
  */
  const runAction = {
    label: author.loading ? 'Building…' : 'Build the comparison from this question',
    onClick: () => author.build({ question, datasetIds }),
    disabled: !canAuthor,
    footer: (
      <>
        {/* **Named, not spun at.** The generation loop runs to a minute and a
            half on a real dataset, and a spinner that long reads as a hang —
            the author's next move is to press the button again, which starts a
            second generation and abandons the first. Raised in review of
            #177. */}
        {author.slow && (
          <span className="resui-field-helper">
            Still building — this can take a minute.
          </span>
        )}
        {!author.loading && question.length > 0 && datasetIds.length === 0 && (
          <span className="resui-field-helper">Choose at least one dataset first.</span>
        )}
        {author.error && (
          <div className="resui-banner resui-banner--alert alert-inline-block">
            <Icon name="error_outline" />
            <span className="resui-banner-text">{author.error}</span>
          </div>
        )}
      </>
    ),
  };

  return (
  <div className="alert-step-content">
    <ReportStepQuery
      formData={formData}
      onSetFormData={onSetFormData}
      title="Query and threshold"
      nameLabel="Alert name"
      // No example. This previously read "e.g. Chronic overtime, per member",
      // which names first-customer vocabulary in the first control every author
      // sees — exactly what AC4 rules out, placeholders included. A prompt for
      // the thing itself carries the same help without putting a domain word on
      // screen.
      namePlaceholder="Name this alert"
      queryLabel="What should this alert look at?"
      // **The division of labour, said before it is needed.** A question that
      // states its own limit used to be *refused*: it generated SQL with a
      // HAVING, which the IR vocabulary could not express, so the author who
      // wrote the most natural sentence for an alert was turned away by it.
      // Storing SQL accepts that sentence (PRD-1405), so this is guidance now
      // rather than a warning about a refusal — but it is still the right
      // division, because a limit in the question is a limit the threshold
      // control cannot show or edit afterwards.
      queryPlaceholder="Describe what to measure and over what period"
      queryHelper={
        'The question names what to measure and over what period. '
        + 'The limit that decides when the alert fires is set below, not here.'
      }
      runAction={runAction}
      previewLabel="The query this alert will run"
      preview={(
        <AlertQueryPreview
          sql={alert.sql}
          columns={alert.resultColumns}
          rows={alert.previewRows}
          matchCount={alert.previewRowCount}
          loading={author.loading}
          substitutions={alert.substitutions}
        />
      )}
    />

    <AlertThresholdFields
      formData={formData}
      onSetFormData={onSetFormData}
      building={author.loading}
      onConfirmValues={(values) => author.confirm({ question, datasetIds, values })}
      onUseValues={(values) => author.accept({ question, datasetIds, values })}
    />

    <AlertSpecProblems problems={problems} heading="Before you continue" />

    <AlertMatchCount
      result={dryRun?.result}
      error={dryRun?.error}
      loading={dryRun?.loading}
      ready={dryRun?.ready}
      notReadyReason={notReadyReason}
    />
  </div>
  );
};

export default AlertStepQuery;
